import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import Toast from './Toast';
import styles from './ToastContainer.module.css';

// Errors and warnings interrupt; success and info wait their turn.
const isUrgent = (toast) => toast.type === 'error' || toast.type === 'warning';

// What a screen reader hears for a toast: its title, message and job id, never its buttons.
function Announcement({ title, message, jobId }) {
  return (
    <p>
      {title && `${title}: `}
      {message}
      {jobId && ` · Job ID ${jobId}`}
    </p>
  );
}

// Two live regions mounted, empty, with the container: screen readers often skip text
// inserted together with its region, so a toast's text goes into one that already exists.
// Each toast is appended as its own node (aria-atomic=false, aria-relevant=additions):
// two toasts in a row are both read, even with the same text, where replacing the text
// could drop the first or, for identical text, read nothing. A toast's node leaves with
// the toast, and removals are not read.
function LiveRegion({ toasts, ...live }) {
  return (
    <div className={styles.liveRegion} aria-atomic="false" aria-relevant="additions" {...live}>
      {toasts.map((toast) => <Announcement key={toast.id} {...toast} />)}
    </div>
  );
}

let sequence = 0;

const ToastContainer = ({ position = 'top-right' }) => {
  const [toasts, setToasts] = useState([]);

  const addToast = useCallback((toast) => {
    // Two toasts fired in the same millisecond still get their own ids.
    sequence += 1;
    const id = `${Date.now()}-${sequence}`;
    const newToast = {
      id,
      duration: 5000,
      ...toast
    };

    setToasts(prevToasts => [...prevToasts, newToast]);

    return id;
  }, []);

  const removeToast = useCallback((id) => {
    setToasts(prevToasts => prevToasts.filter(toast => toast.id !== id));
  }, []);

  // Expose addToast and removeToast functions to window for global access
  React.useEffect(() => {
    window.addToast = addToast;
    window.removeToast = removeToast;
    return () => {
      delete window.addToast;
      delete window.removeToast;
    };
  }, [addToast, removeToast]);

  return (
    <>
      <div className={`${styles.container} ${styles[position]}`}>
        {toasts.map(toast => (
          <Toast
            key={toast.id}
            {...toast}
            onClose={removeToast}
          />
        ))}
      </div>
      <LiveRegion role="status" aria-live="polite" toasts={toasts.filter((t) => !isUrgent(t))} />
      <LiveRegion role="alert" toasts={toasts.filter(isUrgent)} />
    </>
  );
};

ToastContainer.propTypes = {
  position: PropTypes.oneOf(['top-right', 'top-left', 'bottom-right', 'bottom-left', 'top-center', 'bottom-center'])
};

export default ToastContainer;
